import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

// MultiInputHelper is a configurable class, that helps to build MultiInput components.
//
// Input:
// * values - array of values of type T
// Output:
// * value - value that input widget should show
// * placeholder - a placeholder for input widget
//
// Configurations:
// * isEqual - function that defines to compare objects
// * isBlank - function that determines if a value is blank/empty (mostly relevant for strings)
// * display - function that is used to display an object in READ_ONLY mode.

type State<T> =
    | { kind: "EMPTY" }
    | { kind: "DIFFERENT_VALUES" }
    | { kind: "IDENTICAL_VALUES"; value: T };

type IsEqualFn<T> = (a: T, b: T) => boolean;
type IsBlankFn<T> = (val: T) => boolean;
type DisplayFn<T, Displayed> = (value: T) => Displayed;

interface MultiInputHelperArgs<T, D> {
    isEqual: IsEqualFn<T>;
    isBlank: IsBlankFn<T>;
    display: DisplayFn<T, D>;
}

class MultiInputHelper<T, D> {
    private isEqual: IsEqualFn<T>;
    private isBlank: IsBlankFn<T>;
    private display: DisplayFn<T, D>;

    constructor(args: MultiInputHelperArgs<T, D>) {
        this.isEqual = args.isEqual;
        this.isBlank = args.isBlank;
        this.display = args.display;
    }

    deriveValueAndPlaceholder(values: Array<T> | undefined): {
        value: T | undefined;
        placeholder: string;
    } {
        const state = this.deriveState(values);
        const placeholder = this.derivePlaceholderFromState(state);
        const value = this.deriveValueFromState(state);
        return { value, placeholder };
    }

    deriveReadOnlyValueAndType(values: Array<T> | undefined): {
        value: D | string;
        type: "secondary" | undefined;
    } {
        const state = this.deriveState(values);

        switch (state.kind) {
            case "EMPTY": {
                const text = t("Form_No_Field_Set");
                return {
                    value: text,
                    type: "secondary",
                };
            }
            case "DIFFERENT_VALUES": {
                const text = t("Form_Field_Multiple_Values");
                return {
                    value: text,
                    type: "secondary",
                };
            }
            case "IDENTICAL_VALUES": {
                return {
                    value: this.display(state.value),
                    type: undefined,
                };
            }
            default: {
                return assertNever(state);
            }
        }
    }

    derivePlaceholder(values: Array<T> | undefined): string {
        const state = this.deriveState(values);
        return this.derivePlaceholderFromState(state);
    }

    deriveValue(values: Array<T> | undefined): T | undefined {
        const state = this.deriveState(values);
        return this.deriveValueFromState(state);
    }

    deriveState(values: Array<T> | undefined): State<T> {
        if (
            values === undefined ||
            values.length === 0 ||
            values.every(this.isBlank)
        ) {
            return { kind: "EMPTY" };
        }
        if (this.areAllEqual(values)) {
            return { kind: "IDENTICAL_VALUES", value: values[0] };
        }
        return { kind: "DIFFERENT_VALUES" };
    }

    private derivePlaceholderFromState(state: State<T>): string {
        switch (state.kind) {
            case "EMPTY":
                return "";
            case "DIFFERENT_VALUES":
                return t("Form_Field_Multiple_Values");
            case "IDENTICAL_VALUES":
                return "";
            default:
                return assertNever(state);
        }
    }

    private deriveValueFromState<T>(state: State<T>): T | undefined {
        switch (state.kind) {
            case "EMPTY":
                return undefined;
            case "DIFFERENT_VALUES":
                return undefined;
            case "IDENTICAL_VALUES":
                return state.value;
            default:
                return assertNever(state);
        }
    }

    private areAllEqual(values: Array<T>): boolean {
        return values.every((value) => this.isEqual(value, values[0]));
    }
}

export { MultiInputHelper };
